import React, { useState, useEffect } from "react";
import ProfileMenu from "./ProfileMenu";
import { calculateTotalPrice } from "../utils/CommonMethod";
import { stockService, profileService, logoCache } from "../services/apiService";

const LOGO_UPDATED_EVENT = "clientLogoUpdated";

export const dispatchLogoUpdate = (dataUrl) => {
  logoCache.set(dataUrl || null);
  window.dispatchEvent(new CustomEvent(LOGO_UPDATED_EVENT, { detail: dataUrl || "/logo.png" }));
};

const useClientLogo = () => {
  // Read from memory cache immediately — populated during login, no flicker
  const [logoUrl, setLogoUrl] = useState(() => logoCache.get() || "/logo.png");

  useEffect(() => {
    // If already loaded from cache, no need to re-fetch
    if (logoCache.get()) return;

    profileService.getClientLogo()
      .then((blob) => {
        if (!blob || blob.size === 0) return;
        const reader = new FileReader();
        reader.onloadend = () => dispatchLogoUpdate(reader.result);
        reader.readAsDataURL(blob);
      })
      .catch(() => {});

    const onLogoUpdated = (e) => setLogoUrl(e.detail);
    window.addEventListener(LOGO_UPDATED_EVENT, onLogoUpdated);
    return () => window.removeEventListener(LOGO_UPDATED_EVENT, onLogoUpdated);
  }, []);

  return logoUrl;
};

const BillingBadge = () => {
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    stockService.getBillingStatus()
      .then(setBilling)
      .catch(() => setBilling(null));
  }, []);

  if (!billing) return null;
  const days = billing.daysLeft ?? 0;
  let bgColor, textColor, icon, message;
  if (days < 0) {
    bgColor = "bg-black"; textColor = "text-white"; icon = "fa-exclamation-circle";
    message = `Your subscription expired ${Math.abs(days)} day(s) ago. Please renew to continue using the service.`;
  } else if (days <= 7) {
    bgColor = "bg-red-100 border border-red-300"; textColor = "text-red-700"; icon = "fa-exclamation-triangle";
    message = `Your subscription expires in ${days} day(s). Renew now to avoid service interruption.`;
  } else if (days <= 15) {
    bgColor = "bg-yellow-100 border border-yellow-300"; textColor = "text-yellow-700"; icon = "fa-clock";
    message = `Your subscription expires in ${days} day(s). Plan: ${billing.plan || "Standard"}.`;
  } else {
    bgColor = "bg-green-100 border border-green-200"; textColor = "text-green-700"; icon = "fa-check-circle";
    message = `Subscription active. ${days} day(s) remaining. Plan: ${billing.plan || "Standard"}.`;
  }

  return (
    <div className="relative group">
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold cursor-default ${bgColor} ${textColor} transition-all hover:shadow-md`}>
        <i className={`fas ${icon} text-[10px]`} />
        {days < 0 ? "Expired" : `${days}d left`}
      </span>
      <div className="absolute hidden group-hover:block top-full right-0 mt-2 w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl z-50 leading-relaxed">
        <div className="flex items-start gap-2">
          <i className={`fas ${icon} mt-0.5 flex-shrink-0`} />
          <span>{message}</span>
        </div>
        {billing.expiryDate && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-700 text-gray-300">
            Expiry: {new Date(billing.expiryDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        )}
        <div className="absolute -top-1.5 right-4 w-3 h-3 bg-gray-900 rotate-45" />
      </div>
    </div>
  );
};

const Header = ({
  showSearch = false,
  showCart = false,
  onSearch,
  cart = [],
  addToCart,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const companyName = localStorage.getItem("companyName") || "Jaya Shree";
  const logoUrl = useClientLogo();

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (onSearch) onSearch(value);
  };

  return (
    <header className="relative bg-gradient-to-r from-blue-100 via-indigo-100 to-teal-100 shadow-md rounded-xl pl-12 md:pl-4 pr-4 py-2.5 flex flex-row items-center justify-between border border-blue-200">

      {/* Title centered when search is hidden */}
      {!showSearch && (
        <div className="absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
          <span className="text-xl sm:text-2xl bg-gradient-to-r from-teal-500 via-purple-400 to-blue-400 text-transparent bg-clip-text animate-pulse font-extrabold">
            {companyName}
          </span>
        </div>
      )}

      {/* Logo with animated icons */}
      <div className="w-11 h-11 rounded-xl overflow-hidden shadow-lg border-2 border-yellow-400/50 bg-gradient-to-br from-yellow-50 to-white p-0.5">
              <img
                src={logoUrl}
                alt="JaShBill"
                className="w-full h-full object-contain rounded-lg"
                onError={(e) => { e.target.src = "/logo.png"; }}
              />
            
        {/*<span className="relative w-8 h-8 rounded-full overflow-hidden flex items-center justify-center shadow border border-blue-200">
          <span className="absolute inset-0 animate-gradient-move bg-[length:300%_300%] bg-gradient-to-r from-pink-300 via-yellow-200 to-teal-300 z-0 rounded-full" />
          <span className="relative z-10 flex gap-[2px] text-white text-[9px] drop-shadow-md">
            <i className="fas fa-capsules animate-ping-slow"></i>
            <i className="fas fa-prescription-bottle-alt animate-bounce-slow"></i>
            <i className="fas fa-syringe animate-pulse"></i>
          </span>
        </span>*/}

        {/* Title when search is visible */}
        {showSearch && (
          <span className="text-xl sm:text-2xl bg-gradient-to-r from-teal-500 via-purple-400 to-blue-400 text-transparent bg-clip-text animate-pulse font-extrabold">
            {companyName}
          </span>
        )}
      </div>

      {/* Search bar */}
      {showSearch && (
        <input
          type="text"
          placeholder="🔍 Search medicine, order ID..."
          className="flex-1 md:w-1/3 border border-blue-200 px-4 py-2 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={searchInput}
          onChange={handleInputChange}
        />
      )}

      {/* Right-side icons */}
      <div className="flex items-center space-x-3 text-blue-700">

        {/* Billing Badge */}
        <BillingBadge />

        {/* Cart Icon */}
        {showCart && (
          <div className="relative group">
            <button className="hover:text-blue-900 transition relative">
              <i className="fas fa-shopping-cart text-2xl"></i>
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center shadow">
                  {cart.length}
                </span>
              )}
            </button>

            {/* Cart Hover Popup */}
            <div className="absolute right-0 mt-2 w-[400px] max-h-[350px] overflow-y-auto z-50 bg-white border border-blue-200 rounded-xl shadow-2xl hidden group-hover:block transition-all duration-300">
              <div className="text-sm font-medium text-center text-blue-800 py-2 border-b bg-blue-50">
                🛒 Cart Preview
              </div>
              {cart.length === 0 ? (
                <div className="text-center py-4 text-gray-500 italic">No items in cart</div>
              ) : (
                <ul className="text-sm text-gray-800 divide-y max-h-[300px] overflow-auto">
                  {cart.map((item, index) => (
                    <li key={index} className="flex justify-between items-center gap-2 px-3 py-2 hover:bg-blue-50">
                      <div className="flex items-center gap-3 flex-1">
                        <input type="checkbox" checked={true} onChange={() => addToCart(item)} className="accent-blue-600" />
                        <span className="truncate font-medium text-blue-900">{item.name}</span>
                      </div>
                      <div className="w-20 text-right text-blue-700 font-semibold">₹{item.sellPrice}</div>
                      <div className="w-14 text-blue-600 text-sm text-center font-medium">x{item.quantity}</div>
                      <div className="w-24 text-right text-green-600 font-bold">
                        ₹{calculateTotalPrice(item.sellPrice, item.quantity).toFixed(2)}
                      </div>
                    </li>
                  ))}
                  <div className="px-4 py-3 bg-blue-50 border-t border-blue-200 text-right text-sm font-bold text-blue-900 shadow-inner">
                    Total: ₹{cart.reduce((acc, item) => acc + calculateTotalPrice(item.sellPrice, item.quantity), 0).toFixed(2)}
                  </div>
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Profile section */}
        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <ProfileMenu />
          </div>
        </div>

        {/* Mobile fallback icon */}
        <button className="hover:text-blue-900 transition md:hidden">
          <i className="fas fa-user text-2xl"></i>
        </button>
      </div>
    </header>
  );
};

export default Header;
